/**
 * The one table from a backend error `code` to a sentence in the reader's language.
 *
 * ## Why this file exists
 *
 * `docs/I18N.md` states the contract: *"Error bodies keep their stable `code`; the UI translates
 * by code and falls back to the server's `message` when a code is unknown. That is why `code`
 * exists."* `ApiError` has carried a `messageKey` for exactly this since it was written, and
 * nothing ever set one from an error envelope — so every refusal the API wrote reached an Italian
 * reader in English. This is the table that was missing.
 *
 * ## The two rules it follows
 *
 * **1. A code is mapped only when its English message is *fixed*.** The catalogue is keyed by code,
 * not by sentence, so the moment two call sites share a code and write different sentences, the one
 * Italian translation can only say what the *code* means — which is coarser than what the server
 * said. That is acceptable where the code is narrow (`AI_NOT_CONFIGURED` is four sentences about
 * one fact: nothing is configured) and unacceptable where it is wide. Concretely, this is why
 * `BAD_REQUEST`, `FORBIDDEN` and `ROUTE_NOT_FOUND` are **not** here: they are the *default* codes of
 * `badRequest()` / `forbidden()` / the route tail, carrying whatever sentence a call site wrote, and
 * translating them by code would replace a specific explanation with a shrug. `VALIDATION_FAILED`
 * looks like one of those and is not — see its entry below.
 *
 * **2. An unmapped code falls back to the server's English, and that is correct.** It is the
 * documented behaviour for a code nobody has translated yet, not a gap. There are ~450 distinct
 * codes on this API; this table covers the ones a person actually meets.
 *
 * ## Adding a code
 *
 * Read what the backend throws for it — *every* call site, not one — and only then decide whether
 * one sentence can honestly stand for all of them. If it can, add the key to `en.json` and
 * `it.json` and the line here. If it cannot, leave it out: the English fallback tells the truth.
 *
 * Where the backend puts a number or a name in `error.details`, the sentence may name it with a
 * placeholder — `lib/api.ts` passes the scalar entries of `details` through to `t()`. Only do that
 * when the detail is *always* present for that code, because a placeholder with nothing to fill it
 * renders as `{limit}` on screen.
 */

import type { SimpleTKey } from './catalog'

/**
 * Code → key. Deliberately `Partial<Record<string, …>>` rather than a closed union: the keys are
 * wire values from another process, and a lookup that misses is the normal, documented path.
 */
export const ERROR_CODE_KEYS: Readonly<Partial<Record<string, SimpleTKey>>> = {
  /* ── the AI surface ──────────────────────────────────────────────────────────
     The complete set of `AiErrorKind` → HTTP codes from `modules/ai/http.ts`, which is the one
     place that decides what a provider failure means over HTTP. Five modules raise these — the
     assistant, media generation, the hashtag tool, the batch scheduler and Flows' natural-language
     authoring — so each sentence says what the *kind* is, not what one caller was doing. */
  AI_NOT_CONFIGURED: 'common.apiErrors.aiNotConfigured',
  AI_CREDITS_EXHAUSTED: 'common.apiErrors.aiCreditsExhausted',
  AI_PROVIDER_REJECTED: 'common.apiErrors.aiProviderRejected',
  /* The server may add `retryAfterSeconds` here, and may not — `AiError` leaves it undefined for a
     timeout or an unreachable provider. An optional detail cannot go in the sentence. */
  AI_PROVIDER_UNAVAILABLE: 'common.apiErrors.aiProviderUnavailable',
  AI_REFUSED: 'common.apiErrors.aiRefused',
  AI_EMPTY: 'common.apiErrors.aiEmpty',
  AI_FAILED: 'common.apiErrors.aiFailed',
  AI_JOB_NOT_FOUND: 'common.apiErrors.aiJobNotFound',
  AI_NO_NETWORKS: 'common.apiErrors.aiNoNetworks',
  AI_JOB_STATE: 'common.apiErrors.aiJobState',
  AI_BATCH_INVALID: 'common.apiErrors.aiBatchInvalid',

  /* ── Flows ───────────────────────────────────────────────────────────────────
     Called *automations* everywhere a person can read — `flows` is the wire name and the module
     name, and appears in no sentence below. */
  FLOW_NOT_FOUND: 'common.apiErrors.flowNotFound',
  /** `details.name` is the name the author typed, always present. */
  FLOW_NAME_TAKEN: 'common.apiErrors.flowNameTaken',
  /** `details.limit` is `MAX_FLOWS_PER_BRAND`, always present — a static sentence could not say it. */
  FLOW_LIMIT: 'common.apiErrors.flowLimit',
  FLOW_CAMPAIGN_NOT_ON_BRAND: 'common.apiErrors.flowCampaignNotOnBrand',
  FLOW_POST_NOT_ON_BRAND: 'common.apiErrors.flowPostNotOnBrand',
  FLOW_POST_NOT_WATCHABLE: 'common.apiErrors.flowPostNotWatchable',
  FLOW_POST_NOT_A_DRAFT: 'common.apiErrors.flowPostNotADraft',
  FLOW_STATE_NOT_ON_BRAND: 'common.apiErrors.flowStateNotOnBrand',
  FLOW_PERSON_NOT_ON_BRAND: 'common.apiErrors.flowPersonNotOnBrand',

  /* ── drafts and feed sources ─────────────────────────────────────────────────
     `modules/drafts/errors.ts` maps five kinds onto five codes, and each kind is thrown with
     several sentences, so each sentence here states the kind. */
  DRAFTS_NOT_FOUND: 'common.apiErrors.draftsNotFound',
  DRAFTS_CONFLICT: 'common.apiErrors.draftsConflict',
  DRAFTS_INVALID: 'common.apiErrors.draftsInvalid',
  DRAFTS_NOT_A_FEED: 'common.apiErrors.draftsNotAFeed',
  DRAFTS_NO_NETWORKS: 'common.apiErrors.draftsNoNetworks',

  /* ── media generation ────────────────────────────────────────────────────────
     Its provider failures are `AI_*` above; these two are its own. */
  MEDIA_CREDIT_PACK_UNKNOWN: 'common.apiErrors.mediaCreditPackUnknown',
  /* The server's own words name Stripe and a missing url — an operator's sentence, not a reader's. */
  STRIPE_SESSION_WITHOUT_URL: 'common.apiErrors.stripeSessionWithoutUrl',

  /* ── session and account ─────────────────────────────────────────────────────
     Every one of these is thrown with one fixed sentence, from `middleware/requireAuth.ts` and the
     two JWT branches of `middleware/errorHandler.ts`. */
  UNAUTHORIZED: 'common.apiErrors.unauthorized',
  TOKEN_EXPIRED: 'common.apiErrors.tokenExpired',
  INVALID_TOKEN: 'common.apiErrors.invalidToken',
  ACCOUNT_NOT_FOUND: 'common.apiErrors.accountNotFound',
  ACCOUNT_SUSPENDED: 'common.apiErrors.accountSuspended',
  SUPERADMIN_REQUIRED: 'common.apiErrors.superadminRequired',

  /* ── role and permission ─────────────────────────────────────────────────────
     Both carry their specifics in `details` as machine values — `required: ['OWNER','ADMIN']`, a
     permission slug — which are not words a reader knows, so neither sentence names them. */
  ROLE_REQUIRED: 'common.apiErrors.roleRequired',
  PERMISSION_REQUIRED: 'common.apiErrors.permissionRequired',

  /* ── workspace and brand scoping ─────────────────────────────────────────────
     `middleware/requireOrg.ts` and `middleware/requireBrand.ts`, one fixed sentence each. */
  ORG_NOT_FOUND: 'common.apiErrors.orgNotFound',
  ORG_FORBIDDEN: 'common.apiErrors.orgForbidden',
  ORG_REQUIRED: 'common.apiErrors.orgRequired',
  NO_ORGANIZATION: 'common.apiErrors.noOrganization',
  ORG_SUSPENDED: 'common.apiErrors.orgSuspended',
  BRAND_ID_REQUIRED: 'common.apiErrors.brandIdRequired',
  BRAND_NOT_FOUND: 'common.apiErrors.brandNotFound',
  BRAND_OUT_OF_SCOPE: 'common.apiErrors.brandOutOfScope',

  /* ── connections with no stored grant ────────────────────────────────────────
     One fixed sentence each, one call site each — `tokensOf()` in
     `modules/connections/tokens.ts` and `adTokensOf()` in `modules/ads/tokens.ts`. Both throw
     when a row has no token to decrypt at all, which `resolveTokens()` otherwise lets escape
     uncaught on the branch that never calls `tokensOf` (a connection that is "fresh" by
     `needsRefresh()`'s measure but was never actually authorised). */
  CONNECTION_NOT_AUTHORISED: 'common.apiErrors.connectionNotAuthorised',
  AD_ACCOUNT_NOT_AUTHORISED: 'common.apiErrors.adAccountNotAuthorised',

  /* ── billing ───────────────────────────────────────────────────────────────
     One call site (`assertDowngradeAllowed` in `modules/billing/service.ts`), one sentence
     *shape* — not literally fixed text, but the three numbers it names (`limit`, `current`,
     `toDelete`) are always present in `details`, which is what the judgment-call rule for
     `VALIDATION_FAILED` above says makes a parameterised sentence honestly mappable. */
  BRAND_LIMIT_EXCEEDED_BY_DOWNGRADE: 'common.apiErrors.brandLimitExceededByDowngrade',

  /* ── rate limiting ───────────────────────────────────────────────────────────
     `middleware/rateLimit.ts` writes both envelopes itself, so both sentences are fixed. They stay
     two keys because they are two different waits: a moment, and a minute. */
  RATE_LIMITED: 'common.apiErrors.rateLimited',
  TOO_MANY_ATTEMPTS: 'common.apiErrors.tooManyAttempts',

  /* ── the generic envelopes `errorHandler.ts` writes itself ───────────────────
     Safe to translate precisely because no module throws them: each is one branch of `normalise()`
     with one hard-coded sentence. `DATABASE_ERROR` and the Prisma `BAD_REQUEST` are left out —
     both are `expose: false`, so in production they already answer with `INTERNAL_ERROR`'s
     sentence, and in development their English names the real cause for the developer reading it. */
  NOT_FOUND: 'common.apiErrors.notFound',
  ALREADY_EXISTS: 'common.apiErrors.alreadyExists',
  RELATED_RECORD_MISSING: 'common.apiErrors.relatedRecordMissing',
  /**
   * The judgment call.
   *
   * This code looks unmappable — it wraps Zod, and Zod's text differs per field and per request.
   * It is mappable, and the reason is where that text lives: `fromZod` puts the per-field issues in
   * `details.fields` and sets `message` to one fixed sentence, *"Some fields need attention."*
   * Nothing arbitrary is being translated here; the arbitrary part is not in `message` at all.
   *
   * The second half of the check is that no module borrows the code: `unprocessable()` defaults to
   * `VALIDATION_FAILED`, and all 34 call sites in `backend/src` pass an explicit code instead, so
   * the Zod handler is its only producer. That is what makes one sentence honest.
   *
   * `details.fields` is not interpolated. Its keys are schema paths (`consentGiven`, `blockIds.0`),
   * which are English identifiers rather than the labels beside the inputs, and there are usually
   * several — there is no one field to name. A form that wants per-field messages should read
   * `error.details` itself, next to the inputs it owns, which is where the label lives.
   */
  VALIDATION_FAILED: 'common.apiErrors.validationFailed',
  MALFORMED_JSON: 'common.apiErrors.malformedJson',
  FILE_TOO_LARGE: 'common.apiErrors.fileTooLarge',
  DATABASE_UNAVAILABLE: 'common.apiErrors.databaseUnavailable',
  INTERNAL_ERROR: 'common.apiErrors.internalError',
}

/** The key for a code, or null when nobody has translated it — which is the documented fallback. */
export const errorKeyFor = (code: string): SimpleTKey | null => ERROR_CODE_KEYS[code] ?? null
